import React, { useCallback, useState } from 'react';
import ashokaImg from '../../assets/images/ashoka-ias.webp';
import cureoImg from '../../assets/images/cureo-homeopathy.webp';
import dimaImg from '../../assets/images/dima-niwas.webp';
import kangkanImg from '../../assets/images/kangkan_app_showcase.jpg';
import { Container, Eyebrow, PillButton } from './ui';
import LeaveSiteDialog from './LeaveSiteDialog';

const projects = [
  {
    title: 'Kangkan – Ecommerce App',
    description:
      'An Android shopping app for authentic Assamese handloom (Mekhela Chador, Muga and Assam silk sarees, jewellery and home decor), live on Google Play.',
    tags: ['E-commerce', 'Android App', 'Live on Google Play'],
    image: kangkanImg,
    imageAlt: 'Kangkan app screens on Android phones',
    imagePosition: 'object-center',
    liveUrl: 'https://play.google.com/store/apps/details?id=com.kangkan.app&hl=en',
    kind: 'app',
  },
  {
    title: 'Ashoka IAS Academy',
    description:
      'The complete online home of a UPSC & APSC coaching institute, built to bring in admissions and keep aspirants coming back.',
    tags: ['Coaching Institute', 'Admissions', 'Live Website'],
    image: ashokaImg,
    liveUrl: 'https://www.ashokaiasacademy.com/',
  },
  {
    title: 'Cureo Homeopathy Care',
    description:
      'A warm, trust-first website for a doctor-led homeopathy clinic that turns visitors into booked consultations.',
    tags: ['Healthcare', 'Appointment Booking', 'Live Website'],
    image: cureoImg,
    liveUrl: 'https://cureohomeopathy.in/',
  },
  {
    title: 'Dima Niwas Campsite',
    description:
      'A nature-first website for a lakeside campsite and restaurant, built to show off the stay and bring bookings straight to WhatsApp.',
    tags: ['Hospitality', 'WhatsApp Bookings', 'Live Website'],
    image: dimaImg,
    liveUrl: 'https://www.dimaniwascampsite.in/',
  },
];

// Straight, full-bleed screenshot filling the whole panel.
const ProjectVisual = ({ image, title, alt, position = 'object-top' }) => (
  <div className="relative aspect-video lg:aspect-auto lg:flex-1 lg:min-h-[290px] rounded-2xl overflow-hidden bg-[#e6e9f0]">
    <img
      src={image}
      alt={alt || `${title} website homepage`}
      loading="lazy"
      className={`absolute inset-0 w-full h-full object-cover ${position}`}
    />
    <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-black/5 pointer-events-none" />
  </div>
);

const ProjectsShowcase = () => {
  const [leaving, setLeaving] = useState(null);
  const closeDialog = useCallback(() => setLeaving(null), []);

  return (
    <section id="projects" className="bg-[#e9ecf2] pb-10 lg:pb-20">
      <Container>
        <div className="text-center">
          <Eyebrow>My Portfolio</Eyebrow>
          <h2 className="mt-3 text-[34px] sm:text-[44px] lg:text-[48px] font-medium leading-[1.05] tracking-[-0.04em] text-[#111]">
            Projects That Delivered
            <br />
            Real Impact
          </h2>
        </div>

        <div className="mt-12 space-y-6">
          {projects.map((p, i) => (
            <div key={p.title}>
              <article className="relative grid grid-cols-1 lg:grid-cols-2 gap-6 rounded-3xl bg-[#f4f6fa] p-4 sm:p-5 shadow-[0_20px_50px_rgba(17,24,58,0.08)] lg:min-h-[400px]">
                <div className="flex flex-col justify-between gap-10 p-2 sm:p-3">
                  <h3 className="text-2xl sm:text-[28px] font-medium tracking-[-0.03em] text-[#111]">{p.title}</h3>
                  <div>
                    <p className="text-[15px] leading-relaxed text-[#111]/70 max-w-[400px]">{p.description}</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {p.tags.map((t) => (
                        <span key={t} className="text-xs text-[#111]/70 bg-[#e6e9f0] rounded-full px-3 py-1">
                          {t}
                        </span>
                      ))}
                    </div>
                    <PillButton type="button" onClick={() => setLeaving({ title: p.title, url: p.liveUrl, kind: p.kind })} className="mt-6">
                      Explore Project
                    </PillButton>
                  </div>
                </div>

                <div className="flex flex-col">
                  <span className="hidden lg:block self-end -mt-1 mb-2 mr-1 text-[56px] leading-none font-semibold tracking-tight text-[#111]/[0.08] select-none">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <ProjectVisual image={p.image} title={p.title} alt={p.imageAlt} position={p.imagePosition} />
                </div>
              </article>
            </div>
          ))}
        </div>
      </Container>
      <LeaveSiteDialog target={leaving} onClose={closeDialog} />
    </section>
  );
};

export default ProjectsShowcase;
